<template>
    <common-page content-class="w-100">
        <div class="position-relative d-flex flex-row w-100">
            <drawer-handler is-left to-invisible
                            class="flex-shrink-0"
                            close-width="1"
                            @open="resizeChart(300)">
                <div class="d-flex flex-column card p-1 h-100" style="width: max-content">
                    <b-button class="w-100 mb-2" variant="outline-secondary"
                              :disabled="diagramLoading || checkLoading"
                              @click="addNewChart">
                        <b-spinner small v-if="diagramLoading"></b-spinner>
                        + New Chart
                    </b-button>
                    <div class="flex-grow-1 overflow-auto">
                        <div v-for="key of Object.keys(groupedNode)" :key="key" class="d-flex flex-column mb-2">
                            <h3 @click="addGroupToChart(key)">{{ key }}</h3>
                            <template v-for="(item, index) of groupedNode[key]">
                                <b-button variant="light" class="text-nowrap" :key="index"
                                          :class="nodeIsSelected(item.relatedId)?'text-info':''"
                                          @click="addNodeToChart(item)">
                                    {{ item.label }}
                                </b-button>
                            </template>
                        </div>
                    </div>

                    <div class="mt-auto" v-if="lastSaveDate">
                        Last Saved:
                        <br>
                        {{ lastSaveDate.format('YYYY-MM-DD HH:mm:ss') }}
                    </div>
                </div>
            </drawer-handler>
            <div class="flex-grow-1 ml-1 d-flex flex-column h-100" v-if="!diagramLoading">
                <div v-for="(item, i) of charts" :key="i" class="w-100 card mb-1 position-relative"
                     :style="{height: 100/charts.length + '%'}"
                     @click="selectChart(i)">
                    <ComputedLineChart ref="lineChart" class="w-100 h-100"
                                       not-merge
                                       :title="item.title"
                                       :node-ids="item.nodeIds"
                                       :title-map="nodeNameMap">
                    </ComputedLineChart>
                    <b-badge v-if="currentChartIndex === i" class="position-absolute top left" variant="success">
                        Pick to Insert Here
                    </b-badge>
                    <div class="position-absolute top right cursor-pointer" @click="deleteChart(i)">
                        <i class="fa-solid fa-xmark"></i>
                    </div>
                </div>
            </div>
        </div>
    </common-page>
</template>

<script>
import CommonPage from "@/components/echarts-part/stuff/CommonPage.vue";
import ComputedLineChart from "@/components/LayoutMaps/openLayers/stuff/ComputedLineChart.vue";
import jsonShareCenterMixins from "@/mixin/common/json-share-center-mixins";
import dayjs from "dayjs";
import DrawerHandler from "@/components/echarts-part/CalendarShift/cells/stuffs/DrawerHandler.vue";

const KEY = 'custom-diagram-data-charts';

export default {
    name: "DataLineChartsPage",
    components: {DrawerHandler, ComputedLineChart, CommonPage},
    mixins: [jsonShareCenterMixins],
    data() {
        return {
            diagramLoading: false,
            nodeList: [],
            nodeNameMap: new Map(),

            charts: [],
            currentChartIndex: -1,

            lastSaveDate: null,
        }
    },
    computed: {
        diagramCode() {
            return this.$route.query.diagramCode;
        },
        groupedNode() {
            let map = {};
            this.nodeList.forEach(o => {
                let key = o.parentLabel;
                if (key in map) {
                    map[key].push(o);
                } else {
                    map[key] = [o];
                }
            })
            return map;
        },
    },
    watch: {
        charts: {
            handler() {
                this.update();
                this.resizeChart();
            },
            deep: true
        }
    },
    mounted() {
        if (this.diagramCode) {
            this.getDiagramData();
            this.$_check(KEY, this.diagramCode, null)
                .then(res => {
                    if (res.hasNew) {
                        this.charts = JSON.parse(res.configuration.configuration);
                        this.lastSaveDate = dayjs(res.configuration.lastModifyTime);
                    }
                });
        } else {
            this.$message.error("Error")
        }

        let startDate = this.$route.query.startDate;
        if (startDate) {
            this.$store.commit('customizedDiagram/setComputedHistoryQuery', {
                startDate: startDate,
            })
        }
    },
    beforeDestroy() {
        this.$store.commit('customizedDiagram/setComputedHistoryQuery', null);
    },
    methods: {
        dayjs,
        getDiagramData() {
            this.diagramLoading = true;
            this.$store.dispatch('api/get', {
                url: process.env.VUE_APP_EM_ARCHIVE_URL + "/emSicCustomizedDiagramScd/getPopupList?diagramCode=" + this.diagramCode
            }).then(res => {
                this.nodeList = res;
                for (let node of this.nodeList) {
                    this.nodeNameMap.set(`${node.relatedId}`, node.label);
                }
            }).finally(() => this.diagramLoading = false);
        },

        addNewChart() {
            this.charts.push({
                nodeIds: []
            })
            this.currentChartIndex = this.charts.length - 1;
            this.resizeChart();
        },
        deleteChart(i) {
            this.charts.splice(i, 1);
            this.resizeChart();
        },
        resizeChart(delay = null) {
            if (delay) {
                setTimeout(() => {
                    this.resizeChart();
                }, delay);
            } else {
                this.$nextTick(() => {
                    let charts = this.$refs.lineChart;
                    if (charts) {
                        if (charts instanceof Array) {
                            for (let el of charts) {
                                el.resizeChart();
                            }
                        } else {
                            charts.resizeChart();
                        }
                    }
                });
            }

        },
        selectChart(index) {
            if (this.currentChartIndex === index) {
                this.currentChartIndex = -1;
            } else {
                this.currentChartIndex = index;
            }
        },
        tryGetCurrentChart() {
            if (this.currentChartIndex >= 0 && this.currentChartIndex < this.charts.length) {
                return this.charts[this.currentChartIndex];
            }
            return null;
        },
        addNodeToChart(node, noDelete = false) {
            let chart = this.tryGetCurrentChart();
            if (chart) {
                let index = chart.nodeIds.findIndex(o => o === node.relatedId);
                if (index >= 0) {
                    !noDelete && chart.nodeIds.splice(index, 1);
                } else {
                    chart.nodeIds.push(node.relatedId);
                }
                this.updateChartTitle(chart);
            }
        },
        addGroupToChart(key) {
            let list = this.groupedNode[key];
            if (list) {
                for (let item of list) {
                    this.addNodeToChart(item, true);
                }
            }
        },
        updateChartTitle(chart) {
            let title = null;
            for (let nodeId of chart.nodeIds) {
                let node = this.nodeList.find(o => o.relatedId === nodeId);
                if (title == null || node.parentLabel === title) {
                    title = node.parentLabel;
                } else {
                    this.$set(chart, 'title', null);
                    return;
                }
            }
            this.$set(chart, 'title', title);
        },

        nodeIsSelected(nodeId) {
            let chart = this.tryGetCurrentChart();
            return chart && chart.nodeIds.some(o => o === nodeId);
        },

        update() {
            this.$_upload({
                identifyCode: KEY,
                key: this.diagramCode,
                configuration: JSON.stringify(this.charts)
            }).then(() => {
                this.lastSaveDate = dayjs(new Date());
            });
        },
    }
}
</script>

<style scoped>
</style>
